/**
 * Admin Auth Store — Supabase Auth
 * Replaces Firebase Auth for admin login/session management.
 */

import { create } from 'zustand';
import { createClient, type Session, type User } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function getSupabase() {
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
  });
}

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4001/api').replace(/\/$/, '');

interface AdminAuthState {
  session: Session | null;
  supabaseUser: User | null;
  userProfile: Record<string, unknown> | null;
  isAdmin: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: () => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAdminAuthStore = create<AdminAuthState>()((set) => ({
  session: null,
  supabaseUser: null,
  userProfile: null,
  isAdmin: false,
  isLoading: true,
  isInitialized: false,
  error: null,

  initialize: () => {
    const supabase = getSupabase();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session && session.user) {
        const user = session.user;
        // Check role from app_metadata (set by server-side admin)
        const role = user.app_metadata?.role || user.user_metadata?.role || 'customer';
        const isAdmin = role === 'admin' || role === 'super_admin';

        if (!isAdmin) {
          await supabase.auth.signOut();
          set({
            session: null,
            supabaseUser: null,
            userProfile: null,
            isAdmin: false,
            isLoading: false,
            isInitialized: true,
            error: 'Access denied. Admin privileges required.',
          });
          return;
        }

        // Fetch user profile from our API
        let profile: Record<string, unknown> | null = null;
        try {
          const profileUrl = API_BASE.endsWith('/api') ? `${API_BASE}/auth/profile` : `${API_BASE}/api/auth/profile`;
          const profileRes = await fetch(profileUrl, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (profileRes.ok) {
            const data = await profileRes.json();
            profile = data.data || data;
          }
        } catch { /* non-critical */ }

        set({
          session,
          supabaseUser: user,
          userProfile: profile,
          isAdmin: true,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      } else {
        set({
          session: null,
          supabaseUser: null,
          userProfile: null,
          isAdmin: false,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      }
    });

    // Get current session on init — force refresh to pick up latest app_metadata
    supabase.auth.refreshSession().then(({ data: { session }, error }) => {
      if (!session || error) {
        // If refresh failed, fall back to existing session
        supabase.auth.getSession().then(({ data: { session: s } }) => {
          if (!s) set({ isLoading: false, isInitialized: true });
          // else wait for onAuthStateChange
        });
      }
      // If refresh succeeded, onAuthStateChange will fire with updated token
    });

    return () => subscription.unsubscribe();
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) throw error;
      if (!data.session || !data.user) throw new Error('Sign in failed — no session returned');

      let role = data.user.app_metadata?.role || data.user.user_metadata?.role || 'customer';

      // Fallback: check public users table if app_metadata has no role
      if (role === 'customer') {
        try {
          const { createClient: createAnonClient } = await import('@supabase/supabase-js');
          const anonClient = createAnonClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
            auth: { autoRefreshToken: false, persistSession: false }
          });
          const { data: profile } = await anonClient
            .from('users')
            .select('role')
            .eq('email', email)
            .single();
          if (profile?.role) role = profile.role;
        } catch { /* non-critical */ }
      }

      const isAdmin = role === 'admin' || role === 'super_admin';

      if (!isAdmin) {
        await supabase.auth.signOut();
        set({
          error: 'Access denied. Admin privileges required.',
          isLoading: false,
        });
        return;
      }

      set({
        session: data.session,
        supabaseUser: data.user,
        isAdmin: true,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  signOut: async () => {
    const supabase = getSupabase();
    await supabase.auth.signOut();
    set({
      session: null,
      supabaseUser: null,
      userProfile: null,
      isAdmin: false,
      isLoading: false,
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));
